import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo, ease } from "./Primitives";
import { useLang, useT } from "@/lib/i18n";
import { scrollToId } from "@/lib/scroll";

const LINKS = ["services", "portfolio", "blog", "gift", "about", "pricing", "contact"];

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex items-center rounded-full border border-[var(--line)] p-1 text-xs font-mono" data-testid="lang-toggle">
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-btn`}
          onClick={() => setLang(l)}
          className={`px-3 py-1 rounded-full uppercase transition-colors duration-300 ${lang === l ? "bg-[var(--gold)] text-[#0c0a09]" : "text-[var(--muted)] hover:text-[var(--fg)]"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const Nav = () => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (id === "blog") { navigate("/blog"); return; }
    if (pathname !== "/") { navigate("/", { state: { scrollTo: id } }); return; }
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${scrolled || open ? "bg-[rgba(12,10,9,0.72)] backdrop-blur-xl border-b border-[var(--line)]" : ""}`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        <button onClick={() => go("top")} className="flex items-center gap-3" data-testid="nav-logo">
          <Logo />
          <span className="font-display text-xl tracking-tight whitespace-nowrap">Lene Mauro</span>
        </button>
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {LINKS.map((l) => (
            <button key={l} data-testid={`nav-${l}-link`} onClick={() => go(l)} className="nav-link text-sm text-[var(--muted)] hover:text-[var(--fg)] transition-colors">
              {t.nav[l]}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangToggle />
          <span className="hidden md:block">
            <button data-testid="nav-cta-btn" onClick={() => go("contact")} className="btn-gold text-sm">
              {t.nav.cta}
            </button>
          </span>
          <button data-testid="nav-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden p-2" aria-label="menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease }}
            className="lg:hidden overflow-hidden px-5"
          >
            <div className="py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button key={l} data-testid={`mobile-nav-${l}-link`} onClick={() => go(l)} className="font-display text-4xl text-left">
                  {t.nav[l]}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
